import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { trackLead } from "../lib/pixel";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#c9a227";

/**
 * Reusable email capture form. POSTs to /api/subscribe.
 * Variants:
 *   - "footer"  : compact horizontal layout for site footer
 *   - "inline"  : stacked layout for sections
 */
export const EmailCapture = ({
    variant = "footer",
    source = "footer",
    headline = "Get the Sleep Track",
    subline = "Free 8-hour sleep journey. Delivered to your inbox.",
    cta = "Send It",
    testIdPrefix = "footer-email",
}) => {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [state, setState] = useState("idle"); // idle | loading | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
            setState("error");
            setErrorMsg("Please enter a valid email.");
            return;
        }
        setState("loading");
        setErrorMsg("");
        try {
            const res = await fetch(`${API_URL}/api/subscribe`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email,
                    first_name: firstName || null,
                    source,
                    followed_it: false,
                }),
            });
            if (!res.ok) throw new Error("Subscribe failed");
            setState("success");
            setEmail("");
            setFirstName("");
            // Fire Meta Pixel Lead event for ad optimization
            trackLead({
                content_name: "Sleep Track Email",
                content_category: source || "footer",
            });
        } catch (err) {
            setState("error");
            setErrorMsg("Something went wrong. Try again.");
        }
    };

    if (state === "success") {
        return (
            <div
                data-testid={`${testIdPrefix}-success`}
                style={{
                    border: `1px solid ${GOLD}`,
                    padding: "20px 24px",
                    backgroundColor: "rgba(201,162,39,0.06)",
                    maxWidth: variant === "footer" ? 460 : 560,
                }}
            >
                <p
                    style={{
                        color: GOLD,
                        fontSize: 11,
                        letterSpacing: "0.3em",
                        textTransform: "uppercase",
                        margin: "0 0 8px",
                        fontWeight: 600,
                    }}
                >
                    You&apos;re In
                </p>
                <p
                    style={{
                        color: "#fff",
                        fontSize: 14,
                        lineHeight: 1.6,
                        margin: 0,
                        fontWeight: 300,
                    }}
                >
                    Watch your inbox — the sleep track is on its way.
                </p>
            </div>
        );
    }

    const isFooter = variant === "footer";

    return (
        <form
            onSubmit={handleSubmit}
            data-testid={`${testIdPrefix}-form`}
            style={{
                maxWidth: isFooter ? 460 : 560,
                width: "100%",
            }}
        >
            {headline && (
                <h4
                    className="eyebrow"
                    style={{
                        marginBottom: 10,
                        color: GOLD,
                        fontSize: 11,
                        letterSpacing: "0.3em",
                    }}
                >
                    {headline}
                </h4>
            )}
            {subline && (
                <p
                    style={{
                        color: "#bbb",
                        fontSize: 13,
                        lineHeight: 1.65,
                        margin: "0 0 18px",
                        fontWeight: 300,
                    }}
                >
                    {subline}
                </p>
            )}
            <div
                style={{
                    display: "flex",
                    gap: 8,
                    flexDirection: isFooter ? "column" : "column",
                }}
            >
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (state === "error") setState("idle");
                    }}
                    placeholder="your@email.com"
                    data-testid={`${testIdPrefix}-email-input`}
                    style={{
                        backgroundColor: "#0a0a0a",
                        border: "1px solid rgba(201,162,39,0.3)",
                        color: "#fff",
                        padding: "13px 16px",
                        fontSize: 14,
                        fontFamily: "Inter, sans-serif",
                        outline: "none",
                        width: "100%",
                        boxSizing: "border-box",
                        letterSpacing: "0.02em",
                    }}
                    onFocus={(e) =>
                        (e.currentTarget.style.borderColor = GOLD)
                    }
                    onBlur={(e) =>
                        (e.currentTarget.style.borderColor =
                            "rgba(201,162,39,0.3)")
                    }
                />
                <button
                    type="submit"
                    disabled={state === "loading"}
                    data-testid={`${testIdPrefix}-submit`}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                        backgroundColor:
                            state === "loading" ? "#7a6315" : GOLD,
                        color: "#0a0a0a",
                        border: "none",
                        padding: "13px 22px",
                        fontSize: 12,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        cursor:
                            state === "loading" ? "wait" : "pointer",
                        fontFamily: "Inter, sans-serif",
                        whiteSpace: "nowrap",
                    }}
                >
                    {state === "loading" ? "Sending…" : cta}
                    {state !== "loading" && <ArrowRight size={14} />}
                </button>
            </div>
            {state === "error" && (
                <p
                    data-testid={`${testIdPrefix}-error`}
                    style={{
                        color: "#e87878",
                        fontSize: 12,
                        margin: "10px 0 0",
                        letterSpacing: "0.02em",
                    }}
                >
                    {errorMsg}
                </p>
            )}
            <p
                style={{
                    color: "#666",
                    fontSize: 10.5,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    margin: "12px 0 0",
                    fontWeight: 400,
                    lineHeight: 1.5,
                }}
            >
                No spam. Unsubscribe anytime.
            </p>
        </form>
    );
};

export default EmailCapture;
